import { useQuery } from "@apollo/client";
import { isEmpty, orderBy } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { RANKIFIER_REPORT } from "../../../graphql/queries/ReportQueries";
import { GameForReport } from "../GameReport/GameReport";
import { ChartHeader } from "../ChartHeader/ChartHeader";
import { NetScoreChart } from "./NetScoreChart";
import { PositivesVsNegativesChart } from "./PositivesVsNegativesChart";
import { RawScoresTable } from "./RawScoresTable";
import { WithQueryLoader } from "../../shared/WithQueryLoader";

import styles from "./RankifierChart.module.scss";

export interface ChartData {
  name: string;
  score: number;
  positives: number;
  negatives: number;
}

export interface RankifierChartProps {
  game: GameForReport;
}

export const RankifierChart = ({ game }: RankifierChartProps) => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(RANKIFIER_REPORT, {
    variables: {
      gameId: game.id,
    },
  });

  const rankifierData = useMemo(() => {
    if (!data?.rankifierReport) {
      return [];
    }

    const mappedData = data.rankifierReport.attributeResults.map((item) => {
      return {
        name: item.attribute.name,
        score: item.score,
        positives: item.pluses,
        negatives: item.minuses,
      };
    });

    return orderBy(mappedData, "score", "desc");
  }, [data]);

  if (isEmpty(rankifierData)) {
    return <div className={styles.emptyWrapper}>{t("report:noData")}</div>;
  }

  return (
    <WithQueryLoader isLoading={loading}>
      <div className={styles.container}>
        <ChartHeader respondersCount={data?.rankifierReport.totalResponses}>{t("report:rankifier.net")}</ChartHeader>

        <NetScoreChart data={rankifierData} />
      </div>

      <div className={styles.container}>
        <ChartHeader respondersCount={data?.rankifierReport.totalResponses}>
          {t("report:rankifier.negativesVsPositivies")}
        </ChartHeader>

        <PositivesVsNegativesChart data={rankifierData} />
      </div>

      <div className={styles.container}>
        <ChartHeader respondersCount={data?.rankifierReport.totalResponses}>
          {t("report:rankifier.rawScores")}
        </ChartHeader>

        <RawScoresTable data={rankifierData} />
      </div>
    </WithQueryLoader>
  );
};
